import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { booksAPI, chatAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    BookOpen, 
    Video, 
    GraduationCap, 
    ArrowRight,
    Calendar,
    Users,
    Award,
    ChevronRight,
    Plane,
    Star,
    CheckCircle
} from 'lucide-react';

export default function Home() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [bookInfo, setBookInfo] = useState(null);
    const [courseInfo, setCourseInfo] = useState(null);
    const [nextSession, setNextSession] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [bookRes, courseRes, sessionsRes] = await Promise.all([
                productsAPI.getBookInfo(),
                productsAPI.getCourseInfo(),
                chatAPI.getSessions()
            ]);
            
            setBookInfo(bookRes.data);
            setCourseInfo(courseRes.data);
            
            // Find next upcoming session
            const upcomingSessions = sessionsRes.data.filter(
                s => new Date(s.scheduled_at) > new Date()
            ).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
            
            if (upcomingSessions.length > 0) {
                setNextSession(upcomingSessions[0]);
            }
        } catch (err) {
            console.error('Failed to load data:', err);
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const stagger = {
        visible: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative py-16 md:py-24 overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Text Content */}
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={stagger}
                            className="space-y-6"
                        >
                            <motion.div variants={fadeInUp}>
                                <Badge variant="secondary" className="mb-4">
                                    <Plane className="h-3 w-3 mr-1" />
                                    Your Dream Career Awaits
                                </Badge>
                            </motion.div>
                            
                            <motion.h1 
                                variants={fadeInUp}
                                className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight"
                            >
                                Become a 
                                <span className="text-primary block mt-2">Flight Attendant</span>
                            </motion.h1>
                            
                            <motion.p 
                                variants={fadeInUp}
                                className="text-lg text-muted-foreground max-w-lg"
                            >
                                Get the complete guide to launching your cabin crew career. 
                                Learn from industry expert Shelly with our book, live coaching classes, and community support.
                            </motion.p>
                            
                            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 pt-4">
                                <Button 
                                    size="lg" 
                                    className="rounded-full gap-2"
                                    onClick={() => navigate('/book')}
                                    data-testid="hero-get-book"
                                >
                                    <BookOpen className="h-5 w-5" />
                                    Get the Book - $29.99
                                </Button>
                                <Button 
                                    size="lg" 
                                    variant="outline"
                                    className="rounded-full gap-2"
                                    onClick={() => navigate('/membership')}
                                    data-testid="hero-join-community"
                                >
                                    Join Our Community
                                    <ArrowRight className="h-5 w-5" />
                                </Button>
                            </motion.div>
                        </motion.div>

                        {/* Hero Image */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_read-and-connect-1/artifacts/rmhz8210_IMG_9698.jpeg"
                                    alt="Shelly - Flight Attendant"
                                    className="object-cover w-full h-full"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                                
                                {/* Floating card */}
                                <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur p-4 rounded-xl border shadow-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Plane className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">By Shelly</p>
                                            <p className="text-sm text-muted-foreground">
                                                Industry Expert & Coach
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="py-16 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="text-center mb-12"
                    >
                        <motion.h2 
                            variants={fadeInUp}
                            className="font-heading text-3xl md:text-4xl font-bold mb-4"
                        >
                            Best Sellers Options
                        </motion.h2>
                        <motion.p 
                            variants={fadeInUp}
                            className="text-muted-foreground max-w-2xl mx-auto"
                        >
                            Our most popular products to help you launch your flight attendant career
                        </motion.p>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
                    >
                        {/* Book Card */}
                        <motion.div variants={fadeInUp}>
                            <Card 
                                className="p-6 h-full hover:shadow-lg transition-shadow cursor-pointer group border-2 hover:border-primary/50"
                                onClick={() => navigate('/book')}
                                data-testid="feature-book"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <BookOpen className="h-7 w-7 text-primary" />
                                    </div>
                                    <Badge className="bg-green-500">Best Seller</Badge>
                                </div>
                                <h3 className="font-heading text-2xl font-semibold mb-2">The Book</h3>
                                <p className="text-primary font-bold text-3xl mb-3">$29.99</p>
                                <p className="text-muted-foreground mb-4">
                                    "Your Dream Job: Flight Attendant Interview Success" - 
                                    The complete guide to launching your cabin crew career.
                                </p>
                                <ul className="space-y-2 mb-6">
                                    {['Step-by-step career guide', 'Interview preparation tips', 'Industry insider secrets', 'Resume & application help'].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Button className="w-full rounded-full group-hover:gap-3 transition-all">
                                    Get the Book <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Card>
                        </motion.div>

                        {/* Course Card */}
                        <motion.div variants={fadeInUp}>
                            <Card 
                                className="p-6 h-full hover:shadow-lg transition-shadow cursor-pointer group border-2 hover:border-primary/50"
                                onClick={() => navigate('/course')}
                                data-testid="feature-course"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <Video className="h-7 w-7 text-primary" />
                                    </div>
                                    <Badge variant="secondary">Video Course</Badge>
                                </div>
                                <h3 className="font-heading text-2xl font-semibold mb-2">Master Courses</h3>
                                <p className="text-primary font-bold text-3xl mb-3">$297</p>
                                <p className="text-muted-foreground mb-4">
                                    Access 10+ professional video classes to prepare for your flight attendant career.
                                </p>
                                <ul className="space-y-2 mb-6">
                                    {[
                                        'Access 10+ Classes',
                                        'Under 10 minutes each',
                                        'Unlimited online access',
                                        'Face to face prep',
                                        'Assessment do\'s and don\'ts',
                                        'Online interview tips'
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Button variant="outline" className="w-full rounded-full group-hover:gap-3 transition-all">
                                    View Course <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Card>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Membership Tiers Section */}
            <section className="py-16 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="text-center mb-12"
                    >
                        <motion.h2 
                            variants={fadeInUp}
                            className="font-heading text-3xl md:text-4xl font-bold mb-4"
                        >
                            Membership Tiers
                        </motion.h2>
                        <motion.p 
                            variants={fadeInUp}
                            className="text-muted-foreground max-w-2xl mx-auto"
                        >
                            Join our community and get ongoing support for your flight attendant journey
                        </motion.p>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto"
                    >
                        {/* Basic Tier */}
                        <motion.div variants={fadeInUp}>
                            <Card className="p-6 h-full hover:shadow-lg transition-shadow">
                                <div className="text-center mb-4">
                                    <Badge variant="secondary" className="mb-3">Basic</Badge>
                                    <p className="text-primary font-bold text-3xl">$29<span className="text-lg text-muted-foreground">/mo</span></p>
                                </div>
                                <ul className="space-y-2 mb-6">
                                    {['Community chat access', 'Weekly live Q&A', 'Interview prep resources', 'Member discussions'].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className="w-full rounded-full"
                                    onClick={() => navigate('/membership')}
                                >
                                    Get Basic
                                </Button>
                            </Card>
                        </motion.div>

                        {/* Premium Tier */}
                        <motion.div variants={fadeInUp}>
                            <Card className="p-6 h-full hover:shadow-lg transition-shadow border-2 border-primary relative">
                                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">Most Popular</Badge>
                                <div className="text-center mb-4 mt-2">
                                    <Badge variant="secondary" className="mb-3">Premium</Badge>
                                    <p className="text-primary font-bold text-3xl">$49.99<span className="text-lg text-muted-foreground">/mo</span></p>
                                </div>
                                <ul className="space-y-2 mb-6">
                                    {['All Basic benefits', 'Airlines Hiring Database', 'Personalized advice', 'Priority Q&A access', 'Exclusive resources'].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className="w-full rounded-full"
                                    onClick={() => navigate('/membership')}
                                >
                                    Get Premium
                                </Button>
                            </Card>
                        </motion.div>

                        {/* VIP Tier */}
                        <motion.div variants={fadeInUp}>
                            <Card className="p-6 h-full hover:shadow-lg transition-shadow bg-gradient-to-b from-stone-100 to-white dark:from-stone-800/30 dark:to-background border-stone-300 dark:border-stone-600">
                                <div className="text-center mb-4">
                                    <Badge className="mb-3 bg-stone-600">VIP</Badge>
                                    <p className="text-primary font-bold text-3xl">$99<span className="text-lg text-muted-foreground">/mo</span></p>
                                    <p className="text-xs text-green-600 mt-1 font-medium">Save $10 with 2-month signup!</p>
                                </div>
                                <ul className="space-y-2 mb-6">
                                    {['All Premium benefits', 'Free 30-min coaching/mo', '20% off all coaching', 'VIP-only sessions', 'Direct access to Shelly', '2-month commitment required'].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                                            <CheckCircle className="h-4 w-4 text-stone-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className="w-full rounded-full bg-stone-600 hover:bg-stone-700 text-white"
                                    onClick={() => navigate('/membership')}
                                >
                                    Go VIP
                                </Button>
                            </Card>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mt-8"
                    >
                        <Button 
                            variant="link" 
                            className="text-primary"
                            onClick={() => navigate('/membership')}
                        >
                            Compare all membership features <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </motion.div>
                </div>
            </section>
            {nextSession && (
                <section className="py-12">
                    <div className="container mx-auto px-4">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <Card className="p-6 md:p-8 bg-primary text-primary-foreground">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-start gap-4">
                                        <div className="h-12 w-12 rounded-xl bg-primary-foreground/20 flex items-center justify-center flex-shrink-0">
                                            <Calendar className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h3 className="font-heading text-xl font-semibold mb-1">
                                                Next Live Class: {nextSession.title}
                                            </h3>
                                            <p className="opacity-90">
                                                {new Date(nextSession.scheduled_at).toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    <Button 
                                        variant="secondary"
                                        size="lg"
                                        className="rounded-full"
                                        onClick={() => navigate('/live-classes')}
                                        data-testid="next-session-join"
                                    >
                                        View Schedule
                                    </Button>
                                </div>
                            </Card>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* About Section */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <img
                                src="https://customer-assets.emergentagent.com/job_read-and-connect-1/artifacts/3zqz6n1n_IMG_1499.jpeg"
                                alt="Shelly"
                                className="rounded-2xl shadow-lg"
                            />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <h2 className="font-heading text-3xl md:text-4xl font-bold">
                                Learn from an Industry Expert
                            </h2>
                            <p className="text-muted-foreground">
                                Shelly has helped hundreds of aspiring flight attendants 
                                land their dream jobs at top airlines. With years of experience in 
                                the aviation industry, she knows exactly what recruiters are looking for.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { icon: Users, value: '1000+', label: 'CJOs Achieved' },
                                    { icon: Star, value: '4.9', label: 'Average Rating' },
                                    { icon: Award, value: '95%', label: 'Success Rate' },
                                    { icon: Plane, value: '10+', label: 'Years Experience' },
                                ].map((stat, i) => (
                                    <div key={stat.label} className="text-center p-4 bg-muted/50 rounded-lg">
                                        <stat.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                                        <div className="font-heading text-2xl font-bold">{stat.value}</div>
                                        <div className="text-sm text-muted-foreground">{stat.label}</div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-16 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            What Our Students Say
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Hear from real students who landed their dream jobs
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {[
                            {
                                name: "Sarah M.",
                                role: "Flight Attendant at Delta Airlines",
                                image: "SM",
                                rating: 5,
                                text: "Shelly's book was my bible during the application process! Every tip was spot-on. The interview chapter alone is worth 10x the price. I got hired on my first try!",
                                type: "Book"
                            },
                            {
                                name: "Jessica T.",
                                role: "Flight Attendant at United Airlines",
                                image: "JT",
                                rating: 5,
                                text: "The live coaching sessions changed everything for me. Shelly did mock interviews with me and pointed out things I never would have noticed. Her feedback was invaluable!",
                                type: "Live Classes"
                            },
                            {
                                name: "Amanda R.",
                                role: "Flight Attendant at American Airlines",
                                image: "AR",
                                rating: 5,
                                text: "I failed 3 interviews before finding Shelly. After her one-on-one session, I understood exactly what I was doing wrong. Two weeks later, I got the call - I was hired!",
                                type: "One-on-One"
                            },
                            {
                                name: "Michelle K.",
                                role: "Flight Attendant at Southwest Airlines",
                                image: "MK",
                                rating: 5,
                                text: "The book covers everything from resume tips to what to wear. Shelly's insider knowledge of what airlines really look for gave me such an advantage over other candidates.",
                                type: "Book"
                            },
                            {
                                name: "Rachel P.",
                                role: "Flight Attendant at JetBlue",
                                image: "RP",
                                rating: 5,
                                text: "The weekly live classes are incredible! Being able to ask Shelly questions in real-time and hear other students' experiences helped me feel so prepared. Best investment ever!",
                                type: "Live Classes"
                            },
                            {
                                name: "Emily W.",
                                role: "Flight Attendant at Alaska Airlines",
                                image: "EW",
                                rating: 5,
                                text: "Shelly's one-on-one coaching was a game-changer. She helped me craft my personal story and practice my answers until they felt natural. I walked into my interview with total confidence!",
                                type: "One-on-One"
                            }
                        ].map((testimonial, i) => (
                            <motion.div
                                key={testimonial.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <Card className="p-6 h-full">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                            {testimonial.image}
                                        </div>
                                        <div>
                                            <p className="font-medium">{testimonial.name}</p>
                                            <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 mb-3">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                        ))}
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">"{testimonial.text}"</p>
                                    <Badge variant="secondary" className="text-xs">{testimonial.type}</Badge>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-muted/30">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <Plane className="h-12 w-12 text-primary mx-auto mb-6" />
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            Ready to Take Off?
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                            Start your journey to becoming a flight attendant today. 
                            Get the book, join our community, and land your dream job.
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Button 
                                size="lg" 
                                className="rounded-full"
                                onClick={() => navigate('/book')}
                                data-testid="cta-get-book"
                            >
                                Get the Book - $29.99
                            </Button>
                            <Button 
                                size="lg" 
                                variant="outline"
                                className="rounded-full"
                                onClick={() => navigate('/membership')}
                                data-testid="cta-community"
                            >
                                Join Community
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
